// Blog coverflow carousel.
//
// One continuous float `pos` drives everything: at integer k, slide k is fully
// active; between k and k+1 slide k shrinks and slide k+1 grows. layout(pos) sizes
// every slide by its distance to pos and offsets the track so `pos` sits at the
// viewport centre. Because that offset is linear in pos, a drag translates the track
// 1:1 with the finger WHILE the cards resize — a real coverflow feel.
//
// All motion (drag/swipe, arrows, tap-to-centre, keyboard, release-snap) goes through
// animateTo()/layout() and is animated with requestAnimationFrame. There are NO CSS
// transitions on the animated properties. Sizes are read back from the CSS variables
// so CSS stays the single source of truth. Infinite loop via cloned copies each side.
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  // Approximates the CSS cubic-bezier(.4,0,.2,1) feel (fast-out, slow-in).
  function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  function initCarousel(root) {
    if (root.__cfInit) return; // guard against double-binding (editor re-inits)
    root.__cfInit = true;

    var viewport = root.querySelector('.cf__viewport');
    var track = root.querySelector('.cf__track');
    if (!viewport || !track) return;

    var REAL = parseInt(track.getAttribute('data-real'), 10) || 0;
    var PREPEND = parseInt(track.getAttribute('data-prepend'), 10) || 0;
    var START = parseInt(track.getAttribute('data-start'), 10) || 0;
    if (REAL < 1) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('.cf__slide'));
    var cards = slides.map(function (s) { return s.querySelector('.cf__card'); });
    var N = slides.length;

    var prevBtn = root.querySelector('.cf__btn--prev');
    var nextBtn = root.querySelector('.cf__btn--next');

    // Caption is a SIBLING of the carousel (both inside .blog-cf), not a descendant.
    var scope = root.closest('.blog-cf') || document;
    var caption = scope.querySelector('[data-cf-caption]');
    var capTitle = caption && caption.querySelector('.cf__caption-title');
    var capBody = caption && caption.querySelector('.cf__caption-body');
    var capTimer = null, capIdx = -1;

    var pos = PREPEND + START;   // continuous index; integer = that slide centred
    var raf = null;
    var M;                        // cached metrics {aw, iw, gap}

    // Read the active/inactive card widths + gap from the CSS (single source of
    // truth). The viewport's height is `--aw * 3/2`, so aw = viewportHeight * 2/3;
    // the inactive width is aw * --cf-iw-ratio; gap is the track's computed gap.
    function readMetrics() {
      var vpH = viewport.getBoundingClientRect().height;
      var aw = vpH * 2 / 3;
      var cs = getComputedStyle(track);
      var gap = parseFloat(cs.columnGap || cs.gap) || 0;
      var ratio = parseFloat(getComputedStyle(root).getPropertyValue('--cf-iw-ratio')) || 0.62;
      return { aw: aw, iw: aw * ratio, gap: gap };
    }

    // Drag distance that advances pos by exactly one index. The focus centre moves
    // (aw - iw)/2 + gap per index (constant across the range), so mapping the drag
    // through this makes the track translate 1:1 with the finger.
    function dragStep() { return (M.aw - M.iw) / 2 + M.gap; }

    function layout(p) {
      var vpW = viewport.getBoundingClientRect().width; // read before any width writes
      var aw = M.aw, iw = M.iw, gap = M.gap, span = aw - iw;
      var kFloor = Math.floor(p);
      var x = 0, ck = 0, ck1 = 0;
      for (var i = 0; i < N; i++) {
        var dist = Math.abs(i - p);
        var g = dist >= 1 ? 0 : 1 - dist;   // 0 = inactive, 1 = active
        var cw = iw + span * g;
        slides[i].style.width = cw + 'px';
        var card = cards[i];
        if (card) {
          card.style.opacity = (0.5 + 0.5 * g).toFixed(3);
          card.style.setProperty('--cf-dim', (0.28 * (1 - g)).toFixed(3));
        }
        var center = x + cw / 2;
        if (i === kFloor) ck = center;
        else if (i === kFloor + 1) ck1 = center;
        x += cw + gap;
      }
      // Offset so the focus point (interpolated between card kFloor and kFloor+1)
      // lands at the viewport centre.
      var frac = p - kFloor;
      var fc = (kFloor + 1 < N) ? (ck * (1 - frac) + ck1 * frac) : ck;
      track.style.transform = 'translateX(' + (vpW / 2 - fc) + 'px)';

      var active = Math.round(p);
      for (var j = 0; j < N; j++) slides[j].classList.toggle('is-active', j === active);
    }

    // ---- Caption ----
    function writeCaption(idx) {
      if (!capTitle) return;
      var s = slides[idx];
      capTitle.textContent = s ? (s.getAttribute('data-title') || '') : '';
      if (capBody) capBody.textContent = s ? (s.getAttribute('data-desc') || '') : '';
      capIdx = idx;
    }
    // Cross-fade: drop opacity, swap text mid-fade, fade back in (260ms matches the
    // .cf__caption transition in the CSS). Clones carry the same caption as their real
    // card, so wrap jumps never flash.
    function fadeCaption(idx) {
      if (!caption || idx === capIdx) return;
      if (REDUCED) { writeCaption(idx); return; }
      caption.classList.add('is-fading');
      window.clearTimeout(capTimer);
      capTimer = window.setTimeout(function () {
        writeCaption(idx);
        caption.classList.remove('is-fading');
      }, 260);
    }

    // ---- Wrap: keep the settled index inside the real window; the clone jump is
    // invisible because clones are identical and we re-layout instantly. ----
    function normalize() {
      var changed = false;
      while (Math.round(pos) < PREPEND) { pos += REAL; changed = true; }
      while (Math.round(pos) >= PREPEND + REAL) { pos -= REAL; changed = true; }
      if (changed) layout(pos);
    }

    function positionBtns() {
      if (!prevBtn || !nextBtn) return;
      var sR = root.getBoundingClientRect();
      var vR = viewport.getBoundingClientRect();
      var mid = (vR.left - sR.left) + vR.width / 2;
      var half = M.aw / 2 + 18;
      prevBtn.style.left = (mid - half - 40) + 'px'; prevBtn.style.right = 'auto';
      nextBtn.style.left = (mid + half) + 'px'; nextBtn.style.right = 'auto';
    }

    function cancelRaf() { if (raf) { cancelAnimationFrame(raf); raf = null; } }
    function settle() { normalize(); positionBtns(); }

    // Eased tween of pos -> target (arrows, tap-to-centre, release-snap, keyboard).
    function animateTo(target) {
      cancelRaf();
      fadeCaption(Math.round(target));
      if (REDUCED) { pos = target; layout(pos); settle(); return; }
      var startPos = pos, startT = null, dur = 460;
      (function step() {
        raf = requestAnimationFrame(function (t) {
          if (startT === null) startT = t;
          var e = clamp((t - startT) / dur, 0, 1);
          pos = startPos + (target - startPos) * easeInOut(e);
          layout(pos);
          if (e < 1) step();
          else { raf = null; pos = target; layout(pos); settle(); }
        });
      })();
    }

    // ---- Arrows + keyboard ----
    if (prevBtn) prevBtn.addEventListener('click', function () { animateTo(Math.round(pos) - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { animateTo(Math.round(pos) + 1); });
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); animateTo(Math.round(pos) - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); animateTo(Math.round(pos) + 1); }
    });

    // ---- Tap a side card to bring it to centre (active card lets its link through) ----
    slides.forEach(function (slide, i) {
      slide.addEventListener('click', function (e) {
        if (didDrag) return;                 // a drag: the capture guard cancels it
        if (i === Math.round(pos)) return;   // active card: follow its link
        e.preventDefault();
        animateTo(i);
      });
    });

    // ---- Drag / swipe (mouse, touch, pen; all devices) ----
    var didDrag = false;
    var drag = { active: false, decided: false, startX: 0, startY: 0, base: 0, moved: 0, id: null };

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return; // primary button only
      cancelRaf();
      M = readMetrics();                    // fresh sizes at the start of the gesture
      drag.active = true; drag.decided = false;
      drag.startX = e.clientX; drag.startY = e.clientY;
      drag.base = pos; drag.moved = 0; drag.id = e.pointerId;
      didDrag = false;
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!drag.active) return;
      var dx = e.clientX - drag.startX, dy = e.clientY - drag.startY;
      if (!drag.decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;       // wait for clear intent
        if (Math.abs(dy) > Math.abs(dx)) { drag.active = false; return; } // vertical -> page scroll
        drag.decided = true;
        viewport.classList.add('is-grabbing');
        try { viewport.setPointerCapture(drag.id); } catch (_) {}
      }
      drag.moved = dx;
      if (e.cancelable) e.preventDefault();
      pos = drag.base - dx / dragStep();     // 1:1 finger tracking + continuous resize
      layout(pos);
    }, { passive: false });

    function endDrag() {
      if (!drag.active) return;
      drag.active = false;
      viewport.classList.remove('is-grabbing');
      if (!drag.decided) return;             // was a tap: let the click through
      didDrag = Math.abs(drag.moved) > 6;
      animateTo(Math.round(pos));            // ease to the nearest card
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    // A real drag ends in a click; swallow it (capture phase) so it neither navigates
    // the active card's link nor fires tap-to-centre.
    track.addEventListener('click', function (e) {
      if (didDrag) { e.preventDefault(); e.stopPropagation(); didDrag = false; }
    }, true);

    // ---- Init + resize ----
    M = readMetrics();
    writeCaption(Math.round(pos));
    layout(pos);
    positionBtns();

    var rzTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(rzTimer);
      rzTimer = window.setTimeout(function () {
        M = readMetrics();
        layout(pos);
        positionBtns();
      }, 100);
    });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-cf-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Shopify theme editor re-renders a section into a fresh DOM on setting change.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
